import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { scheduleAPI } from '../services/api.ts';
import { toast } from 'react-toastify';
import MapPicker from './MapPicker.tsx';

interface ScheduleItem {
  id: string;
  date: string;
  time: string;
  location: string;
  address: string;
  status?: 'pending' | 'completed' | 'canceled';
  projectName?: string;
  workFlowStatusId?: number;
}

interface PendingSignaturesProps {
  limit?: number;
  showViewAll?: boolean;
  onCountUpdate?: (count: number) => void;
  disableInfiniteScroll?: boolean;
}

const PendingSignatures: React.FC<PendingSignaturesProps> = ({ limit, showViewAll = true, onCountUpdate, disableInfiniteScroll = false }) => {
  const [pendingItems, setPendingItems] = useState<ScheduleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [remindersInProgress, setRemindersInProgress] = useState<Record<string, boolean>>({});
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');
  
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastItemRef = useRef<HTMLDivElement | null>(null);
  
  const pageSize = limit || 5;

  // Format date for display
  const formatDateForDisplay = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return format(date, 'MMM d');
    } catch (e) {
      return 'N/A';
    }
  };

  // Format full date time for display
  const formatDateTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return format(date, 'MMM d, h:mm a');
    } catch (e) {
      return 'N/A';
    }
  };

  // Get the day from date
  const getDay = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return format(date, 'd');
    } catch (e) {
      return '--';
    }
  };

  // Fetch pending signature items
  const fetchPendingSignatures = async (isLoadingMore = false) => {

    if (!isLoadingMore) {
      setLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const response = await scheduleAPI.getPendingSignatureCustomers({
        PageNumber: pageNumber,
        PageSize: pageSize
      });

      const newItems = response.data || [];
      const totalRecords = response.totalRecords || response.data?.length || 0;

      if (isLoadingMore) {
        setPendingItems(prev => [...prev, ...newItems]);
      } else {
        setPendingItems(newItems);
      }

      // Update total count if available
      if (onCountUpdate) {
        onCountUpdate(totalRecords);
      }

      // Check if there might be more data
      if (newItems.length < pageSize) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching pending signatures:', err);
      setError('Failed to load pending signature items');
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Load more data
  const loadMore = () => {
    if (!hasMore || isLoadingMore || disableInfiniteScroll) return;
    setPageNumber(prev => prev + 1);
  };

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (loading || !hasMore) return;

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
        loadMore();
      }
    }, options);

    observerRef.current = observer;

    if (lastItemRef.current) {
      observer.observe(lastItemRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, isLoadingMore]);

  // Fetch data when page number changes
  useEffect(() => {
    fetchPendingSignatures(pageNumber > 1);
  }, [pageNumber]);

  // Send signature reminder
  const handleSendReminder = async (scheduleId: string) => {
    if (remindersInProgress[scheduleId]) return;

    setRemindersInProgress(prev => ({ ...prev, [scheduleId]: true }));

    try {
      await scheduleAPI.sendSignatureReminder(scheduleId);
      toast.success('Signature reminder sent successfully!');
    } catch (err) {
      console.error('Error sending signature reminder:', err);
      toast.error('Failed to send reminder. Please try again.');
    } finally {
      setRemindersInProgress(prev => ({ ...prev, [scheduleId]: false }));
    }
  };

  // Function to handle address click
  const handleAddressClick = (address: string) => {
    setSelectedAddress(address);
    setShowMapPicker(true);
  };

  if (loading && !isLoadingMore) {
    return (
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex justify-between items-center mb-4">
          {showViewAll && (
            <div className="text-sm text-gray-500">
              Loading...
            </div>
          )}
        </div>
        <div className="py-8 text-center">
          <div className="inline-block animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
          <p className="mt-2 text-gray-600">Loading pending signatures...</p>
        </div>
      </div>
    );
  }

  if (error && pendingItems.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex justify-between items-center mb-4">
          {showViewAll && (
            <div className="text-sm text-gray-500">
              Error
            </div>
          )}
        </div>
        <div className="py-4 text-center">
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      {showViewAll && pendingItems.length > 0 && (
        <div className="flex justify-end mb-4">
          <Link to="/pending-signatures" className="text-primary text-sm hover:underline">
            View All
          </Link>
        </div>
      )}

      {pendingItems.length === 0 ? (
        <div className="py-4 text-center">
          <p className="text-gray-600">No signatures pending</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingItems.map((item, index) => {
            const isLastItem = index === pendingItems.length - 1;
            
            return (
              <div 
                key={item.id}
                ref={isLastItem && hasMore ? lastItemRef : null}
                className="bg-white shadow rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between">
                  <div>
                    <div className="flex items-center mb-2">
                      <div className="bg-blue-500 text-white px-2 py-1 rounded mr-2">
                        <span className="text-xs font-bold">{formatDateForDisplay(item.date)}</span>
                      </div>
                      <span className="text-sm text-gray-600">{item.time || "No time specified"}</span>
                    </div>
                    {item.projectName && (
                      <h3 className="font-bold text-primary mb-1">{item.projectName}</h3>
                    )}
                  
                    <p 
                      className="text-xs text-primary cursor-pointer hover:underline flex items-center"
                      onClick={() => item.address && handleAddressClick(item.address)}
                    >
                      {item.address || ''}
                      {item.address && (
                        <svg className="w-3 h-3 ml-1" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                      )}
                    </p>
                  </div>
                  <div className="mt-4 md:mt-0 flex items-center space-x-2">
                    <button
                      onClick={() => handleSendReminder(item.id)}
                      disabled={remindersInProgress[item.id]}
                      className={`px-3 py-1.5 rounded text-sm font-medium ${
                        remindersInProgress[item.id]
                          ? 'bg-gray-200 text-gray-500'
                          : 'bg-primary text-white hover:bg-primary-dark'
                        }`}
                    >
                      {remindersInProgress[item.id] ? 'Sending...' : 'Send Reminder'}
                    </button>
                    <Link
                      to={`/schedule/${item.id}`}
                      className="text-primary hover:text-primary-dark text-sm font-medium"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
          
          {isLoadingMore && (
            <div className="py-4 text-center">
              <div className="inline-block animate-spin h-6 w-6 border-4 border-primary border-t-transparent rounded-full"></div>
              <p className="mt-2 text-sm text-gray-600">Loading more...</p>
            </div>
          )}
        </div>
      )}
      
      {/* Map Picker Modal */}
      {showMapPicker && (
        <MapPicker
          isOpen={showMapPicker}
          onClose={() => setShowMapPicker(false)}
          address={selectedAddress}
        />
      )}
    </div>
  );
};

export default PendingSignatures; 